import Link from '@pinecast/common/Link';
import {useColors, useCSS} from '@pinecast/styles';
import * as React from 'react';

const ListView = ({
  children,
  focusGroup,
  style,
}: {
  children: React.ReactNode;
  // Names the items as a focus group, so that focus stays in the list when an
  // item is deleted (see `FocusRecoveryProvider`). By default the name is this
  // list's own. Give it a name when the list mounts again as it reloads, so
  // that focus can go back into it.
  focusGroup?: string;
  style?: React.CSSProperties;
}) => {
  const css = useCSS();
  const id = React.useId();
  return (
    <ul
      className={css({margin: '-12px 0', padding: 0, ...style})}
      data-focus-group={focusGroup ?? `list${id}`}
    >
      {children}
    </ul>
  );
};

export default ListView;

type Props = {
  forceHardNavigation?: boolean;
  href?: string;
  menu?: React.ReactNode;
  prefix?: React.ReactNode;
  primary: React.ReactNode;
  obvious?: React.ReactNode;
  tabular?: React.ReactNode;
};

export const ListViewItem = ({
  forceHardNavigation,
  href,
  menu,
  prefix,
  primary,
  obvious,
  tabular,
}: Props) => {
  const css = useCSS();
  const designColors = useColors();

  const content = (
    <div
      className={css({
        display: 'grid',
        gap: '4px 4px',
        gridTemplateColumns: '1fr min-content',
        gridTemplateAreas:
          '"Prefix-Obvious Prefix-Obvious" "Primary Primary" "Obvious Tabular"',
        padding: '12px 8px',
      })}
    >
      {prefix && (
        <span
          className={css({
            gridArea: 'Prefix-Obvious',
          })}
        >
          {prefix}
        </span>
      )}
      <div
        className={css({
          gridArea: 'Primary',
          lineHeight: '20px',
          display: 'flex',
        })}
      >
        <strong
          className={css({
            flex: '1 1',
            fontSize: '16px',
            fontWeight: '500',
          })}
        >
          {primary}
        </strong>
        {menu}
      </div>
      {obvious && (
        <span
          className={css({
            gridArea: 'Obvious',
          })}
        >
          {obvious}
        </span>
      )}
      {tabular && (
        <span
          className={css({
            fontVariantNumeric: 'tabular-nums',
            gridArea: 'Tabular',
            textAlign: 'right',
          })}
        >
          {tabular}
        </span>
      )}
    </div>
  );

  let inner: React.ReactNode = content;
  if (href) {
    const className = css({
      display: 'block',
      color: 'inherit',
    });
    if (href.startsWith('/') && !forceHardNavigation) {
      inner = (
        <Link
          className={className}
          href={href}
          style={{textDecoration: 'none'}}
        >
          {content}
        </Link>
      );
    } else {
      inner = (
        <a
          href={href}
          className={css({textDecoration: 'none'}, className)}
          rel="noopener"
          target="_blank"
        >
          {content}
        </a>
      );
    }
  }

  return (
    <li
      className={css(
        {
          listStyle: 'none',
          ':not(:empty) + .ListViewItem': {
            borderTop: `1px solid ${designColors.borderGrayNormal}`,
          },
        },
        'ListViewItem',
      )}
    >
      {inner}
    </li>
  );
};
